// Sprint 35: the mini-game sizes, in one pinned table. Pure constants and one pure function; no I/O.
// Roadmap 55c: a blind guess is worth nothing on average. Rules that keep it so:
//  - points only, flat: never multiplied by the streak or the outbreak, never add or remove a tally;
//  - the rescue is a flat refund (not a halving that grows with the streak);
//  - a skip, a run with no input at all, or leaving the page scores as a fail (0 bonus, 0 refund).
// `test/mini-balance.test.js` pins every number here, so editing one fails the build. Sizes may go down, never up.

export const STEPS_PER_SECOND = 15;

// Sprint 36 sizing (studio/sprints/36/EV.md): the door stays the main skill, so a game is worth less than one door step.
// Rules the sizes follow:
//  - a bonus never reaches the door's smallest step (25): every game's maxBonus is at most 20 (test: mini-balance);
//  - the rescue halves the loss: refund = half the wrong call's loss at streak 1 (bull 100 -> 50, bear and Holden 200 -> 100),
//    and it is only offered on a streak (RESCUE below), because a 50 or 100 lump in the weaker player's hands is what
//    moves the 78 % floor, not the bonus.
// seconds: full seconds that pay (a ride of 120 steps is 8, a dodge of 105 steps is 7); Holden counts bags on the pile instead
// (bags, perBag). perSecond / perBag: points per unit. clean: the extra for a clean ride (bull: the lean never passed 0.7 of the
// way to a throw), no mark lost (bear) or no bag spilled more than a third of its width (Holden).
// refund: what a rescue gives back after a wrong call. Holden is a look of the bear knock: the bear's x2 loss, so the bear's refund.
export const MINI = {
  bull: { steps: 120, seconds: 8, perSecond: 2, clean: 4, refund: 50 },
  bear: { steps: 105, seconds: 7, perSecond: 2, clean: 6, refund: 100 },
  holden: { steps: 120, bags: 8, perBag: 2, clean: 4, refund: 100 },
};

// A rescue ride is only offered to a player on a streak: this many right calls in a row going into the door (the same count
// doorMults reads as dkTallies). Below it the wrong call is simply lost. Pure and flat; the engine asks, the server checks.
export const RESCUE = { minTallies: 4 };
export const rescueOffered = ({ tallies = 0 } = {}) => tallies >= RESCUE.minTallies;

// Bonus after a right call, rescue after a wrong call. `result` comes from the shared game module's replay:
// steps = steps played before the end, bags = bags on the pile (Holden), reachedEnd = still on at the bell / alive at the end
// (Holden's rescue: the line was reached), clean = see above.
// played = false covers a skip, an all-zero trace and a left page: all score 0.
// sizes: a size row to score with instead of the table's (the page scores its claim with the row the server sent, so a page loaded before a
// resize claims the numbers the server pays; the server itself never passes one).
export function miniPoints({ guest, right, steps = 0, bags = 0, reachedEnd = false, clean = false, played = true, sizes = null }) {
  const m = sizes ?? MINI[guest];
  if (!m || !played) return 0;
  if (right) {
    const base = m.bags !== undefined
      ? Math.min(m.bags, Math.max(0, bags)) * m.perBag
      : Math.min(m.seconds, Math.floor(steps / STEPS_PER_SECOND)) * m.perSecond;
    return base + (reachedEnd && clean ? m.clean : 0);
  }
  return reachedEnd ? m.refund : 0;
}

// The best a rider can be paid after a right call, and after a wrong call.
export const maxBonus = guest => {
  const m = MINI[guest];
  return (m.bags !== undefined ? m.bags * m.perBag : m.seconds * m.perSecond) + m.clean;
};
export const maxRescue = guest => MINI[guest].refund;

// The guest pick (the shift seed picks; the engineer builds it, `/verify` shows every line).
// weights: bull and bear are drawn at exactly 50/50, or rule 55c breaks at the door itself.
// doors: the player's doorkeeper door numbers (1 is the warm-up, the last door never hosts the guaranteed guest).
// secondShare: the share of simulated shifts with a second guest (never three; a third roll becomes a plain knock).
export const GUEST = {
  weights: { bull: 1, bear: 1 },
  doors: { min: 2, max: 4 },
  maxPerShift: 2,
  secondShare: { min: 0.1, max: 0.3, nominal: 0.2 },
};

// The ceiling on what a perfect rider who guesses blind may gain (Level B of 55c). Never raised, only tightened.
// guestsPerShift is the nominal 1 + secondShare.nominal.
// Sprint 36: a perfect blind rider is 47.5 a guest door (bonus 20, rescue 50 / 100 / 100 over four outcomes, rescue always offered).
export const EV_CEILING = { perGuestDoor: 50, perShift: 60, guestsPerShift: 1.2 };
